import { createFileRoute, redirect } from "@tanstack/react-router";
import { parseLang } from "@majourneys/shared";
import { BlogShowPage } from "../../../features/blog";
import { blogApi } from "../../../features/blog/api/blog.api";
import { blogKeys } from "../../../features/blog/constants/query-keys";
import { withApi404 } from "../../../lib/route-loader";

/**
 * Prefetch the blog post (and the recent-posts pool) into the query cache in a
 * loader so the two `useSuspenseQuery` hooks in BlogShowContent
 * (`useBlog` + `useRecentPosts`) resolve synchronously during SSR.
 *
 * Without this loader the whole article suspends to the skeleton spinner
 * server-side, so the body HTML — including the crawlable in-body `<a>` anchors
 * and the PageSeo `<title>` / canonical / hreflang / JSON-LD — never reaches
 * search engines or non-JS crawlers (it only materialises after client
 * hydration re-fetches the data). Keyed identically to the hooks so the
 * component reads from cache instead of refetching on the client.
 *
 * `withApi404` turns an unknown slug into a real HTTP 404 instead of a
 * soft-404 (200 + NotFound page), matching the activity canonical route.
 */
/**
 * Retired posts that duplicated a stronger article on the same topic. Two of
 * our own posts competing for one query split Google's signals (the weak
 * Agadir duplicate sat at position ~67 while the keeper ranked ~2), so the
 * duplicates are soft-deleted in the DB and their URLs 301 here. Permanent
 * (301), not the redirect() default 307 — Google only consolidates the old
 * URL's signals into the keeper on a permanent redirect.
 */
const RETIRED_SLUG_REDIRECTS: Record<string, string> = {
  "8-things-to-do-in-agadir-unforgettable-experiences-for-every-traveler":
    "10-best-things-to-do-in-agadir",
  "8-exciting-things-to-do-in-dakhla": "10-best-things-to-do-in-dakhla",
};

export const Route = createFileRoute("/$lang/_layout/blog/$slug")({
  loader: async ({ params, context }) => {
    const target = RETIRED_SLUG_REDIRECTS[params.slug];
    if (target) {
      throw redirect({
        to: "/$lang/blog/$slug",
        params: { lang: params.lang, slug: target },
        statusCode: 301,
      });
    }
    const l = parseLang(params.lang);
    await withApi404(() =>
      context.queryClient.ensureQueryData({
        queryKey: blogKeys.detail(l, params.slug),
        queryFn: () => blogApi.getBySlug(params.slug, l),
      }),
    );
    // Recent posts feed the sidebar + related-posts strip, which are also
    // suspense queries reached from the main content — prime them so the
    // article doesn't suspend server-side. Best-effort: a recent-posts hiccup
    // must not 500 the article page.
    await context.queryClient
      .ensureQueryData({
        queryKey: blogKeys.recent(l),
        queryFn: () => blogApi.recentPosts(l),
      })
      .catch(() => undefined);
  },
  component: BlogShowPage,
});
